import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { IDVerificationStateModel } from './id-verification.models';
import { EmployeeApiService } from '../../_services/employee-service/api/employee-api.service';
import { interval, merge, Subject, takeUntil, tap, timer } from 'rxjs';
import {
  ClearIDVerificationState,
  SetTimedOutState,
  StartPollingIDVerificationStatus,
  StopPollingIDVerificationStatus,
} from './id-verification.actions';
import { SetAppStateFromKycCompleted } from '../app/app.actions';
import { idStatus } from '../../shared/utils/general-utils';

@State<IDVerificationStateModel>({
  name: 'IDVerification',
  defaults: {
    status: null,
    status_reason: '',
    timeout: false,
  },
})
@Injectable()
export class IdVerificationState {
  private readonly employeeApiService = inject(EmployeeApiService);
  readonly store = inject(Store);

  protected readonly idStatus = idStatus;
  private readonly POLL_INTERVAL = 2000;
  private readonly stopPolling$ = new Subject<void>();
  private readonly TIME_OUT = 60 * 1000; // 1 minute
  private readonly timeout$ = timer(this.TIME_OUT).pipe(
    tap(() => {
      this.store.dispatch(new SetTimedOutState());
      this.store.dispatch(new SetAppStateFromKycCompleted());
    }),
  );

  // Combine both stop signals (manual stop and timeout)
  private readonly stopSignal$ = merge(this.stopPolling$, this.timeout$);

  @Selector()
  public static IDVerificationStatus(state: IDVerificationStateModel): IDVerificationStateModel {
    return {
      status: state.status,
      status_reason: state.status_reason,
      timeout: state.timeout,
    };
  }

  queryStatus(ctx: StateContext<IDVerificationStateModel>) {
    this.employeeApiService.getEmployeeIdentityDocument().subscribe({
      next: (data) => {
        if (data) {
          ctx.patchState({ status: data.status, status_reason: data.status_reason });
          if (this.idStatus(data.status).isVerified || this.idStatus(data.status).isRejected) {
            this.stopPolling$.next();
            this.store.dispatch(new SetAppStateFromKycCompleted());
          }
        } else {
          this.stopPolling$.next();
        }
      },
      error: () => {
        this.stopPolling$.next();
        ctx.patchState({ status: undefined, status_reason: '', timeout: false });
      },
    });
  }

  @Action(StartPollingIDVerificationStatus)
  startPollingIDVerificationStatus(ctx: StateContext<IDVerificationStateModel>) {
    ctx.patchState({ timeout: false });
    this.queryStatus(ctx);
    interval(this.POLL_INTERVAL)
      .pipe(takeUntil(this.stopSignal$))
      .subscribe(() => {
        this.queryStatus(ctx);
      });
  }

  @Action(StopPollingIDVerificationStatus)
  stopPollingIDVerificationStatus() {
    this.stopPolling$.next();
  }

  @Action(ClearIDVerificationState)
  clearIDVerificationState({ setState }: StateContext<IDVerificationStateModel>) {
    setState({ status: null, status_reason: '', timeout: false });
  }

  @Action(SetTimedOutState)
  setTimedOutState(ctx: StateContext<IDVerificationStateModel>) {
    ctx.patchState({ timeout: true });
  }
}
